/*
DateFormating mixins

Every method here runs on the APPLICATION clock rather than the browser's — the
zone the API is configured with, published on `api/system/config` and applied
by `@/lib/appTime` (XOL-449; it was a hardcoded Kuala Lumpur before that). See
that module for the contract, for the window before the config lands, and for
why parsing must go through `moment.parseZone` rather than
`utcOffset(x, true)` (XOL-414).

Signatures are unchanged; there are 400+ call sites.
------------------------------------------------------------------------ */
import {settings} from '@/components/Data'
import {
  DATE_SQL_FORMAT,
  DATETIME_SQL_FORMAT,
  toAppTime,
  appNow
} from '@/lib/appTime'

export default {
  methods: {

    now (fmt) {
      if (fmt && typeof fmt === 'string') {
        return appNow().format(fmt)
      } else {
        return appNow()
      }
    },



    today () {
      return appNow().startOf('day')
    },



    year (date = null) {
      if (date) {
        var oDate = toAppTime(date)
        return (oDate === null) ? NaN : oDate.year()
      } else {
        return appNow().year()
      }
    },



    fromToday (value, todaysDate = null) {
      if (value === null) {
        return NaN
      } else {

        var oDate = toAppTime(value)

        if (oDate === null) {
          return NaN
        }

        var oToday = todaysDate ? toAppTime(todaysDate) : appNow()
        return (oToday === null) ? NaN : oDate.diff(oToday, 'days')
      }
    },



    daysDiff (d1, d2) {
      if (d1 === null || d2 === null) {
        return NaN
      } else {

        var oDate = toAppTime(d1)
        var oDate2 = toAppTime(d2)

        return (oDate === null || oDate2 === null)
        ? NaN
        : oDate.diff(oDate2, 'days')
      }
    },



    isOverdue (value) {
      if (value == null) {
        return false
      } else {
        // Was `isNaN(this.fromToday()) ? false : this.fromToday(value) < 0`.
        // The guard was dead: fromToday() with no argument hit moment(undefined)
        // — i.e. now — and returned 0, so isNaN(0) was always false. Dropping it
        // is observably identical, and NaN < 0 is false, so an unparseable value
        // still reads as not-overdue.
        return this.fromToday(value) < 0
      }
    },



    addDate (value, incr = 1, period = 'day', fmt = 'YYYY-MM-DD') {
      if (value == null) {
        return null
      } else {
        var oDate = toAppTime(value)
        return (oDate === null) ? null : oDate.add(Number(incr), period).format(fmt)
      }
    },



    addDateTime (value, incr = 1, period = 'day', fmt = 'YYYY-MM-DD') {
      if (value == null) {
        return null
      } else {
        var oDate = toAppTime(value)
        return (oDate === null) ? null : oDate.add(Number(incr), period).format(fmt)
      }
    },



    subDate (value, incr = 1, period = 'day', fmt = 'YYYY-MM-DD') {
      if (value == null) {
        return null
      } else {
        var oDate = toAppTime(value)
        return (oDate === null) ? null : oDate.subtract(Number(incr), period).format(fmt)
      }
    },



    subDateTime (value, incr = 1, period = 'day', fmt = 'YYYY-MM-DD') {
      if (value == null) {
        return null
      } else {
        var oDate = toAppTime(value)
        return (oDate === null) ? null : oDate.subtract(Number(incr), period).format(fmt)
      }
    },



    contractExpiryDate (value, incr = 1, period = 'years', fmt = 'YYYY-MM-DD') {
      var rawDate = this.addDate(value, incr, period, fmt)
      return this.subDate(rawDate, 1, 'day', fmt)
    },



    formatDate (value, fmt = settings.dateFormat) {
      var oDate = toAppTime(value)
      return (oDate === null) ? null : oDate.format(fmt)
    },



    // Calendar dates only — never widen this to emit a time component.
    // See the header of @/lib/appTime.
    formatDateSql (value, fmt = DATE_SQL_FORMAT) {
      var oDate = toAppTime(value)
      return (oDate === null) ? null : oDate.format(fmt)
    },



    formatDateTime (value, fmt = settings.dateTimeFormat) {
      var oDate = toAppTime(value)
      return (oDate === null) ? null : oDate.format(fmt)
    },



    formatDateTimeSql (value, fmt = DATETIME_SQL_FORMAT) {
      var oDate = toAppTime(value)
      return (oDate === null) ? null : oDate.format(fmt)
    },



    formatFriendlyDate (value, color = false, fmt = settings.dateHumanFormat) {
      var oDate = toAppTime(value)

      let d = (oDate === null) ? null : oDate.from(appNow())

      if (color && value) {
        if (this.isOverdue(value)) {
          d = '<span class="text-danger">' + d + '</span>'
        } else {
          d = '<span class="text-primary">' + d + '</span>'
        }
      }
      return d
    },



    // XOL-593: a DAY-level relative label for a calendar-date value (e.g. a
    // back-datable action_steps.approved_at, stored at 00:00:00). Never an hour
    // count — formatFriendlyDate on a midnight value is what produced
    // "17 hours ago" one minute after approving.
    formatFriendlyDay (value, fmt = settings.dateFormat) {
      var d = toAppTime(value)
      if (d === null) { return null }
      var days = appNow().startOf('day').diff(d.clone().startOf('day'), 'days')
      if (days === 0) { return 'today' }
      if (days === 1) { return 'yesterday' }
      return 'on ' + d.format(fmt)
    },



    // XOL-593: relative label for a recorded decision. `instant` (a real
    // timestamp) wins; a future instant (browser clock behind the API's) is
    // clamped to now so it never reads "in a minute". With no usable instant,
    // falls back to the day-level label of `day`.
    formatDecisionAgo (instant, day) {
      var m = toAppTime(instant)
      if (m === null) { return this.formatFriendlyDay(day) }
      var now = appNow()
      return (m.isAfter(now) ? now : m).from(now)
    },



   arrayOfYears (wayBack) {
      let years = []

      if (Number(wayBack) > 0 && Number(wayBack) < 100) {
        wayBack = Number(wayBack)
      } else {
        wayBack = 10
      }
      let max = appNow().year()
      let min = (max - wayBack)

      if (years.length === 0) {
        if (years.length !== max) {
          for (let y = max; y >= min; y--) {
            years.push(y)
          }
        }
      } // if years.length === 0 ...

      return years
    }
  }

}
